import React, { useState } from 'react';
import { X, Copy, Check, Download, AlertTriangle } from 'lucide-react';
import { createTradingWallet } from '../../services/tradingWalletService';
import { notifyNewTradingWallet } from '../WalletConnection';

// Import EKKO logo - same as in InstantWalletTransfer
const EKKO_LOGO_URL = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png';

// Custom styles to match widget styling
const customStyles = `
  /* Hide number input arrows */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }

  /* Custom scrollbar */
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(36, 195, 91, 0.3);
    border-radius: 2px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(36, 195, 91, 0.5);
  }
`;

interface CreateTradingWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWalletCreated: () => void;
  connectedWalletAddress?: string;
}

const CreateTradingWalletModal: React.FC<CreateTradingWalletModalProps> = ({
  isOpen,
  onClose,
  onWalletCreated,
  connectedWalletAddress
}) => {
  const [step, setStep] = useState<'info' | 'create' | 'backup' | 'complete'>('info');
  const [walletName, setWalletName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [backupString, setBackupString] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [backupConfirmed, setBackupConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const resetState = () => {
    setStep('info');
    setWalletName('');
    setIsCreating(false);
    setBackupString('');
    setPublicKey('');
    setBackupConfirmed(false);
    setCopied(false);
    setError(null);
  };
  
  const handleClose = () => {
    resetState();
    onClose();
  };
  
  const handleCreateWallet = async () => {
    if (!connectedWalletAddress) {
      setError('You must connect a wallet first');
      return;
    }
    
    if (!walletName.trim()) {
      setError('Please provide a name for your trading wallet');
      return;
    }
    
    setIsCreating(true);
    setError(null);
    
    try {
      const { tradingWallet, backupString } = await createTradingWallet(
        connectedWalletAddress, 
        walletName.trim()
      );
      
      setPublicKey(tradingWallet.public_key);
      setBackupString(backupString);
      setStep('backup');
    } catch (err) {
      console.error('Error creating trading wallet:', err);
      setError('Failed to create trading wallet. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(backupString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const downloadBackup = () => {
    const element = document.createElement('a');
    const file = new Blob([
      `EKKO V2 TRADING WALLET BACKUP\n\n` +
      `Public Key: ${publicKey}\n` +
      `Wallet Name: ${walletName}\n` +
      `Created: ${new Date().toLocaleString()}\n\n` +
      `PRIVATE KEY BACKUP:\n${backupString}\n\n` +
      `Important: Store this file in a secure location. Do not share this file with anyone.`
    ], { type: 'text/plain' });
    
    element.href = URL.createObjectURL(file);
    element.download = `ekko-trading-wallet-${publicKey.slice(0, 8)}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    // Automatically mark as confirmed when downloaded
    setBackupConfirmed(true);
  };
  
  const handleComplete = () => {
    if (!backupConfirmed) {
      setError('Please confirm that you have backed up your wallet');
      return;
    }
    
    setStep('complete');
    
    // Notify wallet connection component to refresh trading wallets
    notifyNewTradingWallet();
    
    setTimeout(() => {
      handleClose();
      onWalletCreated();
    }, 2000);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <style>{customStyles}</style>
      
      <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-black text-lg font-semibold">Create Trading Wallet</h3>
          <button 
            onClick={handleClose} 
            className="text-gray-600 hover:text-black transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 custom-scrollbar max-h-[80vh] overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 rounded border border-red-500/40 bg-red-500/10">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            </div>
          )}
          
          {step === 'info' && (
            <div className="space-y-4">
              <div className="p-4 rounded bg-gray-50 border border-[#24c35b]/20">
                <h4 className="font-medium mb-2 text-sm text-[#24c35b]">About Trading Wallets</h4>
                <p className="text-xs text-gray-700 leading-relaxed">
                  Trading wallets are custodial wallets managed by EKKO. They allow you to trade without 
                  having to sign every transaction manually.
                </p>
              </div>
              
              <div className="p-4 rounded bg-gray-50 border border-gray-200 space-y-3 text-xs text-gray-700">
                <p className="text-[#24c35b] font-medium">Benefits of using a trading wallet:</p>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-700">
                  <li>Faster trading execution</li>
                  <li>No transaction signing prompts</li>
                  <li>Segregate trading funds from your main wallet</li>
                </ul>
              </div>
              
              <div className="p-4 rounded bg-amber-50 border border-amber-200 text-xs text-amber-700">
                <p>You'll need to fund this wallet with SOL to pay for transaction fees and tokens you wish to trade.</p>
              </div>
              
              <button 
                onClick={() => setStep('create')}
                className="w-full py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm"
              >
                Continue
              </button>
            </div>
          )}
          
          {step === 'create' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="wallet-name" className="text-sm text-black block mb-2">Wallet Name</label>
                <input
                  type="text"
                  id="wallet-name"
                  value={walletName}
                  onChange={(e) => setWalletName(e.target.value)}
                  className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-200 text-black focus:border-[#24c35b]/50 focus:outline-none transition-all text-sm"
                  placeholder="Enter wallet name"
                  autoComplete="off"
                />
              </div>
              <p className="text-xs text-gray-600">Choose a descriptive name to help identify this trading wallet</p>
              
              <button 
                onClick={handleCreateWallet}
                disabled={isCreating || !walletName.trim()}
                className="w-full py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#24c35b]"></div>
                    Creating...
                  </>
                ) : (
                  'Create Trading Wallet'
                )}
              </button>
            </div>
          )}
          
          {step === 'backup' && (
            <div className="space-y-4">
              <div className="p-4 rounded bg-gray-50 border border-amber-200">
                <h4 className="font-medium mb-2 text-amber-400 text-sm">Important: Backup Your Wallet</h4>
                <p className="text-gray-700 text-xs leading-relaxed">
                  This is the only time you'll see your private key. Copy or download it and keep it in a secure location. 
                  Without this backup, you will not be able to recover your trading wallet.
                </p>
              </div>
              
              <div className="p-3 rounded bg-gray-50 border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-gray-600">Private Key:</span>
                  <button
                    onClick={copyToClipboard}
                    className="text-gray-600 hover:text-[#24c35b] transition-colors p-1"
                    title="Copy to clipboard"
                  >
                    {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                  </button>
                </div>
                <code className="text-xs text-gray-700 block p-3 bg-gray-50 rounded font-mono break-all">{backupString}</code>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={downloadBackup}
                  className="flex-1 py-2 px-3 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs flex items-center justify-center gap-1"
                >
                  <Download size={14} />
                  Download
                </button>
                
                <button
                  onClick={copyToClipboard}
                  className="flex-1 py-2 px-3 rounded border border-gray-200 text-gray-600 hover:text-white hover:border-gray-500 transition-all text-xs flex items-center justify-center gap-1"
                >
                  {copied ? (
                    <>
                      <Check size={14} className="text-green-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copy
                    </>
                  )}
                </button>
              </div>
              
              <div className="p-3 rounded bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="backup-confirm"
                    checked={backupConfirmed}
                    onChange={(e) => setBackupConfirmed(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-200 bg-gray-50 text-[#24c35b] focus:ring-[#24c35b]/30"
                    style={{ accentColor: '#24c35b' }}
                  />
                  <label htmlFor="backup-confirm" className="text-xs text-gray-700">
                    I have securely backed up my private key
                  </label>
                </div>
              </div>
              
              <button
                onClick={handleComplete}
                disabled={!backupConfirmed}
                className="w-full py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Complete Setup
              </button>
            </div>
          )}
          
          {step === 'complete' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-[#24c35b]/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#24c35b]/30">
                <Check size={26} className="text-[#24c35b]" />
              </div>
              <h4 className="text-lg font-semibold text-black mb-3">Wallet Created!</h4>
              <p className="text-gray-600 mb-4 text-sm">
                Your new trading wallet has been created successfully.
              </p>
              <div className="p-3 rounded bg-gray-50 border border-gray-200 text-xs text-center mb-4">
                <p className="text-gray-600 mb-1">Wallet Address:</p>
                <p className="font-mono text-gray-700 break-all">{publicKey}</p>
              </div>
              <p className="text-xs text-[#24c35b] font-medium">
                You can now fund this wallet and start trading.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTradingWalletModal;
